import { createStore, produce } from "solid-js/store";
import { createEffect, createMemo, createRoot, createSignal } from "solid-js";
import { toast } from "../components/Toaster";
import { setOrderStore } from "./order.store";
import { FullProduct } from "../../googleScripts/sheet/types";
import {
  calcExtraUnits,
  calcPromoPrice,
} from "../../googleScripts/sheet/lib/promo";
import { getFullProduct } from "./shop.store";

//TODO: only reference to the product variation should be stored in localstorage/cart
// and refetch all the json data again on browser refresh
// otherwise one could keep products in localstorage cart with out of sync prices
// this  means when a product has a price change the cart should be updated with that price
// also means that when a product is removed / out of stock from the google sheet, the cart should be updated accordingly.

export type CartItem = {
  variationId: string;
  product: FullProduct;
  amount: number;
  price: number;
  freeUnits: number;
  canBeOrdered: boolean;
};

export type CartStore = {
  cartItems: CartItem[];
};

export const [cartStore, setCartStore] = createStore<CartStore>({
  cartItems: [],
});

export const SERVICE_FEE_BELOW = 50;
export const SERVICE_FEE = 5;

const STORAGE_KEY = "cartItems";
const VALIDATE_INTERVAL_MS = 5 * 60 * 1000;

const [state, setState] = createSignal<"initial" | "loading" | "ready">(
  "initial"
);

export { state };

export const {
  totalPrice,
  totalItems,
  hasItems,
  isReady,
  hasServiceFee,
  totalPriceWithServiceFee,
} = createRoot(() => {
  const totalPrice = createMemo(() =>
    cartStore.cartItems.reduce(
      (total, item) =>
        total + calcTotalItemPrice(item.product, item.variationId, item.amount),
      0
    )
  );

  const totalItems = createMemo(() =>
    cartStore.cartItems.reduce((total, item) => total + item.amount, 0)
  );

  const hasItems = createMemo(() => totalItems() > 0);

  const isReady = createMemo(() => state() === "ready");

  const hasProductWithServiceCost = createMemo(() =>
    cartStore.cartItems.some(
      ({ product, variationId, amount }) =>
        amount > 0 && getVariationById(product, variationId)?.service_fee
    )
  );

  const hasServiceFee = createMemo(
    () => totalPrice() < SERVICE_FEE_BELOW && hasProductWithServiceCost()
  );

  const totalPriceWithServiceFee = createMemo(() => {
    const tp = totalPrice();
    return tp > 0 && hasServiceFee() ? tp + SERVICE_FEE : tp;
  });

  return {
    totalPrice,
    totalItems,
    hasItems,
    isReady,
    hasServiceFee,
    totalPriceWithServiceFee,
  };
});

export async function init() {
  setState("loading");

  const cartData: CartItem[] = JSON.parse(
    localStorage.getItem(STORAGE_KEY) || "[]"
  );

  setCartStore("cartItems", cartData);

  await validateCartItems();

  createRoot(() => {
    // save the cart to local storage whenever it changes
    createEffect(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cartStore.cartItems));
    });
  });

  setInterval(() => validateCartItems(), VALIDATE_INTERVAL_MS);

  setState("ready");
}

export async function validateCartItems() {
  //get the latest version of the full products
  const fullProductsLatest = await Promise.all(
    cartStore.cartItems.map((item) =>
      getFullProduct({
        categoryName: item.product.category,
        productSlug: item.product.slug,
        refetch: true,
      }).catch((err) => {
        console.error(err);
        return null;
      })
    )
  );

  setCartStore(
    "cartItems",
    produce((items: CartItem[]) => {
      // iterate backwards so removing an item doesn't skip the next one
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        const product = fullProductsLatest.find(
          (p) => p?.slug === item.product.slug
        );

        //product no longer found
        if (!product) {
          console.warn("product no longer found", item.product.slug);
          items.splice(i, 1);
          continue;
        }

        //variation still valid?
        const variation = getVariationById(product, item.variationId);

        if (!variation) {
          console.warn("variation no longer valid:", item.variationId);
          items.splice(i, 1);
          continue;
        }

        //variation changed to upcoming?
        if (variation.upcoming) {
          console.warn("variation is upcoming:", item.variationId);
          items.splice(i, 1);
          continue;
        }

        //update the latest version of the product and recalc
        item.product = product;
        updateItemAmount(item, item.amount);
      }
    })
  );
}

export function clearStore(withToast: boolean = true) {
  setCartStore("cartItems", []);
  withToast &&
    toast.success("Jouw producten worden niet meer onthouden op deze computer");
}

export function addCartItem({
  product,
  variationId,
  amount = 1,
}: {
  product: FullProduct;
  variationId: string;
  amount?: number;
}) {
  setCartStore(
    "cartItems",
    produce((items: CartItem[]) => {
      const existingItem = items.find(
        (item) => item.variationId === variationId
      );
      if (existingItem) {
        updateItemAmount(existingItem, existingItem.amount + amount);
      } else {
        // amount, price, freeUnits and canBeOrdered are all set by updateItemAmount
        items.push(
          updateItemAmount(
            {
              variationId,
              product,
              amount: 0,
              price: 0,
              freeUnits: 0,
              canBeOrdered: false,
            },
            amount
          )
        );
      }
    })
  );

  toast.success(product.name + " is toegevoegd");
  resetOrderState();
}

export function removeCartItem(id: string) {
  setCartStore("cartItems", (items) =>
    items.filter((item) => item.variationId !== id)
  );
  resetOrderState();
}

export function increaseAmount({
  variationId,
  increment = 1,
}: {
  variationId: string;
  increment?: number;
}) {
  // no need to cap at stock here, updateItemAmount already does that
  const item = findCartItem(variationId);
  item && setAmount({ variationId, amount: item.amount + increment });
}

export function decreaseAmount({
  variationId,
  decrement = 1,
}: {
  variationId: string;
  decrement?: number;
}) {
  const item = findCartItem(variationId);
  item &&
    setAmount({ variationId, amount: Math.max(0, item.amount - decrement) });
}

export function setAmount({
  variationId,
  amount,
}: {
  variationId: string;
  amount: number;
}) {
  setCartStore(
    "cartItems",
    (item) => item.variationId === variationId,
    produce((item: CartItem) => {
      updateItemAmount(item, amount);
    })
  );
  resetOrderState();
}

function findCartItem(variationId: string) {
  return cartStore.cartItems.find((item) => item.variationId === variationId);
}

//whenever the cart changes, set the state of the order back to ready
function resetOrderState() {
  setOrderStore((store) => ({ ...store, state: "ready" }));
}

// Sets the item to the highest amount <= targetAmount for which
// amount + free promo units doesn't exceed the stock, and recalculates the item.
function updateItemAmount(item: CartItem, targetAmount: number) {
  const variation = getVariationById(item.product, item.variationId);
  const promo = variation?.promo;
  // unknown variation -> no stock, so the item can't be ordered
  const stock = variation?.stock ?? 0;

  let amount = 0;
  let freeUnits = calcExtraUnits(amount, promo);

  while (amount < targetAmount) {
    const nextFreeUnits = calcExtraUnits(amount + 1, promo);
    if (amount + 1 + nextFreeUnits > stock) break;
    amount++;
    freeUnits = nextFreeUnits;
  }

  item.amount = amount;
  item.freeUnits = freeUnits;
  item.price = calcTotalItemPrice(item.product, item.variationId, amount);
  item.canBeOrdered = amount + freeUnits < stock;

  return item;
}

export function calcTotalItemPrice(
  product: FullProduct,
  variationId: string,
  amount: number = 1
) {
  const variation = getVariationById(product, variationId);

  const price =
    calcPromoPrice(variation?.price, variation?.promo) || variation?.price || 0;
  return amount * price;
}

export function getVariationById(product: FullProduct, variationId: string) {
  return product.variations.find((v) => v.id === variationId);
}

init();
